import { AlertCircle, ArrowRight, Check, LineChart, Loader2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  getGetBacktestQueryKey,
  useGetBacktest,
} from '@biasafe.queries/backtests-backtest-id/backtests-backtest-id';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type { SoteriaCardVariant } from './SoteriaCard';
import type { BacktestStarted } from '../hooks/useSoteriaChat';

// The backtest goes queued → running → completed | failed. The card polls
// every 3s while the run is in flight and snaps to the terminal state once
// the API reports it. No more forever-spinning placeholder.
const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set(['completed', 'succeeded', 'failed']);

export function BacktestStartedCard({ backtest }: { backtest: BacktestStarted }) {
  const { data } = useGetBacktest(backtest.backtest_id, {
    query: {
      queryKey: getGetBacktestQueryKey(backtest.backtest_id),
      refetchInterval: (q) => {
        const status = (q.state.data?.status ?? 'queued').toLowerCase();
        return TERMINAL_STATUSES.has(status) ? false : POLL_INTERVAL_MS;
      },
      // Refetch immediately on mount so a chat-history card reflects the
      // real current state instead of the stale "queued" we serialized.
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  });

  const status = (
    data?.status ??
    backtest.backtest_status ??
    'queued'
  ).toLowerCase();
  const v = pickVariant(status);

  return (
    <SoteriaCard
      variant={v.variant}
      icon={v.icon}
      iconOverlay={
        v.showSpinner && (
          <Loader2 className="absolute -bottom-1 -right-1 h-3 w-3 animate-spin text-primary" />
        )
      }
      eyebrow={v.eyebrowText}
      title={backtest.strategy_name}
      subtitle={`${backtest.strategy_code} · ${v.subline}`}
      cta={
        <Link
          to={backtest.report_url}
          className={soteriaCardCtaClass(v.variant)}
        >
          {v.buttonText}
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
    />
  );
}

interface Variant {
  variant: SoteriaCardVariant;
  icon: React.ReactNode;
  showSpinner: boolean;
  eyebrowText: string;
  subline: string;
  buttonText: string;
}

function pickVariant(status: string): Variant {
  if (status === 'completed' || status === 'succeeded') {
    return {
      variant: 'success',
      icon: <Check className="h-4 w-4" />,
      showSpinner: false,
      eyebrowText: 'Backtest complete',
      subline: 'Open the report to inspect performance, drawdowns, and risk',
      buttonText: 'View report',
    };
  }
  if (status === 'failed') {
    return {
      variant: 'destructive',
      icon: <AlertCircle className="h-4 w-4" />,
      showSpinner: false,
      eyebrowText: 'Backtest failed',
      subline: 'Open the report for the error trace; you can re-run from there',
      buttonText: 'View details',
    };
  }
  // queued / running / unknown
  return {
    variant: 'default',
    icon: <LineChart className="h-4 w-4" />,
    showSpinner: true,
    eyebrowText: status === 'running' ? 'Backtest running' : 'Backtest queued',
    subline:
      status === 'running'
        ? 'Crunching the universe: usually 30-60 seconds'
        : 'Waiting for a worker to pick it up',
    buttonText: 'View report',
  };
}
